import { authClient, redirectToSignIn } from "../lib/authClient";
import { startApiLog, finishApiLog, vxResponseView, rememberRequestId } from "./api-log";

// One place for the demo backend URL. Production: https://be.demo.veratad.app.
// Development: http://localhost:8080 (the VX endpoints); the older /demo/dcams
// and /demo/add-ons endpoints can be pointed elsewhere with
// REACT_APP_DCAMS_BACKEND_URL when they run on a different local port.
export const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || "https://be.demo.veratad.app";
const DCAMS_BACKEND_URL = process.env.REACT_APP_DCAMS_BACKEND_URL || BACKEND_URL;

const FORBIDDEN_MESSAGE = "The demo is limited to the Veratad Demo organization.";
const REFRESH_MARGIN_MS = 60 * 1000;

export class ApiError extends Error {
  constructor(status, error, message) {
    super(message || error || `Request failed (${status})`);
    this.name = "ApiError";
    this.status = status;
    this.error = error || "Error";
  }
}

// Shape an error from apiFetch into the { error, message } the pages render.
export function describeApiError(err) {
  if (err instanceof ApiError) {
    return { error: err.error, message: err.message };
  }
  return {
    error: "Network Error",
    message: "Could not connect to the verification service. Please try again.",
  };
}

let cachedToken = null;
let cachedExp = 0;

function decodeExp(token) {
  try {
    const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    return payload.exp ? payload.exp * 1000 : 0;
  } catch (e) {
    return 0;
  }
}

export function clearToken() {
  cachedToken = null;
  cachedExp = 0;
}

// VX JWT (15 min). Cached in memory and refreshed ~60 s before it expires.
export async function getToken() {
  if (cachedToken && Date.now() < cachedExp - REFRESH_MARGIN_MS) {
    return cachedToken;
  }
  const { data, error } = await authClient.token();
  if (error || !data?.token) {
    clearToken();
    throw new ApiError(401, "Unauthorized", "Your session has expired. Please sign in again.");
  }
  cachedToken = data.token;
  cachedExp = decodeExp(data.token) || Date.now() + 14 * 60 * 1000;
  return cachedToken;
}

function baseFor(path) {
  return path.startsWith("/demo/dcams") || path.startsWith("/demo/add-ons")
    ? DCAMS_BACKEND_URL
    : BACKEND_URL;
}

// POST JSON to the demo backend with the VX bearer token.
// Resolves with the parsed JSON body on 2xx. Throws ApiError on non-2xx
// (401 clears the token and sends the person back to sign-in; 403 carries
// the organization message), or a plain Error when the network fails.
export async function apiFetch(path, body, meta) {
  let token;
  try {
    token = await getToken();
  } catch (err) {
    redirectToSignIn();
    throw err;
  }

  const url = `${baseFor(path)}${path}`;
  const payload = body ?? {};
  const logId = startApiLog({ path, payload, meta });

  let response;
  try {
    response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });
  } catch (err) {
    finishApiLog(logId, { ok: false, error: { error: "Network Error", message: err.message } });
    throw err;
  }

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    finishApiLog(logId, vxResponseView(path, response.status, data, false));
    if (response.status === 401) {
      clearToken();
      redirectToSignIn();
      throw new ApiError(401, data.error || "Unauthorized", data.message || "Please sign in again.");
    }
    if (response.status === 403) {
      throw new ApiError(403, data.error || "Forbidden", FORBIDDEN_MESSAGE);
    }
    throw new ApiError(
      response.status,
      data.error || "Error",
      data.message || data.error || "Something went wrong. Please try again."
    );
  }

  const data = await response.json();
  rememberRequestId(data);
  finishApiLog(logId, vxResponseView(path, response.status, data, true));
  return data;
}
